import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Panel, PageHeader, Loading, StatusPill } from "@/components/portal/widgets";
import { Button } from "@/components/ui/button";
import { Loader2, History, CalendarPlus, Sun, Mail, CheckCheck, Sunrise } from "lucide-react";
import { fmtDateTime, fmtDayKey, dayKey, todayKey } from "@/lib/time";
import { SetStatusDialog } from "@/components/portal/TripStatusTools";

export const DAY_REVIEW_COUNT_CHANGED = "rs-day-review-count";

const ReviewRow = ({ t, checked, onToggle, onSetStatus, history }) => (
  <li className="flex items-start gap-3 px-5 py-3.5" data-testid={`day-review-row-${t.id}`}>
    {!history && <input type="checkbox" checked={checked} onChange={onToggle} className="mt-1 h-4 w-4 accent-blue-600" data-testid={`day-review-check-${t.id}`} />}
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-white"><Link to={`/portal/trips/${t.id}`} className="hover:text-blue-300 hover:underline" data-testid={`day-review-open-${t.id}`}>{t.rider_name || "—"}</Link>
        <span className="ml-2 font-mono-data text-[11px] font-normal text-slate-500">{t.external_trip_id} · {fmtDateTime(t.scheduled_pickup)}</span>
      </p>
      <p className="truncate text-xs text-slate-400">{t.pickup_address} → {t.dropoff_name || t.dropoff_address}</p>
    </div>
    <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
      <div className="flex items-center gap-2">
        {history && (t.closed_by_status || t.filed_status || t.set_status_reason) && <span className="rounded-full border border-slate-600 px-2 py-0.5 text-[10px] font-semibold text-slate-300" data-testid={`day-review-marked-${t.id}`}>Marked {t.closed_by_status || t.filed_status || t.status}</span>}
        <StatusPill value={t.status} />
      </div>
      <p className="text-slate-500">{t.assigned_driver_name || "No driver"}</p>
      {!history && <button onClick={onSetStatus} data-testid={`day-review-set-status-${t.id}`} className="rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-[11px] font-medium text-blue-300 hover:bg-slate-700">Set status</button>}
    </div>
  </li>
);

export default function DayReviewPage() {
  const [scope, setScope] = useState("review");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState("");
  const [moveTo, setMoveTo] = useState("");
  const [statusTrip, setStatusTrip] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(() => fleetApi.get(`/day-review?scope=${scope}`).then((r) => {
    setData(r.data);
    if (scope === "review") window.dispatchEvent(new CustomEvent(DAY_REVIEW_COUNT_CHANGED, { detail: r.data.review_count ?? (r.data.trips || []).length }));
  }).catch((e) => { toast.error(fleetError(e, "Couldn't load Day Review.")); setData({ trips: [] }); }), [scope]);

  useEffect(() => { setData(null); setSel({}); load(); }, [load]);

  const after = () => { setSel({}); load(); };

  if (!data) return <Loading />;
  const trips = data.trips || [];
  const history = scope === "history";
  const chosen = trips.filter((t) => sel[t.id]).map((t) => t.id);
  const allOn = trips.length > 0 && chosen.length === trips.length;
  const today = todayKey();

  const move = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "history") await fleetApi.post("/day-review/action", { trip_ids: chosen, action: "history" });
      else await fleetApi.post("/trips/move-date", { trip_ids: chosen, date: moveTo || today, unassign: true });
      toast.success(`${chosen.length} trip${chosen.length === 1 ? "" : "s"} ${kind === "history" ? "moved to history" : `added to ${!moveTo || moveTo === today ? "today" : fmtDayKey(moveTo)} — waiting for a driver`}.`);
      after();
    } catch (e) { toast.error(fleetError(e, "Couldn't move those trips.")); } finally { setBusy(""); }
  };

  const startDay = async () => {
    if (!window.confirm("Start a new day now? All trips from today move to Day Review (nothing unfinished stays on the board), old alerts clear and tomorrow's standing orders are added.")) return;
    setBusy("start");
    try { await fleetApi.post("/day-review/run-now", { early: true }); toast.success("New day started."); after(); }
    catch (e) { toast.error(fleetError(e, "Couldn't start a new day yet.")); } finally { setBusy(""); }
  };

  const sendTest = async () => {
    try { await api.post("/day/review-email/send-now"); toast.success(trips.length ? "Review email on its way to your inbox." : "Nothing is waiting, so no email was sent."); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const groups = trips.reduce((m, t) => { const d = dayKey(t.scheduled_pickup) || "unknown"; return { ...m, [d]: [...(m[d] || []), t] }; }, {});
  const last = data.last_rollover;
  const tab = (id, label) => (
    <button onClick={() => setScope(id)} data-testid={`day-review-tab-${id}`} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${scope === id ? "bg-blue-700 text-white" : "text-slate-300 hover:bg-slate-800"}`}>{label}</button>
  );

  return (
    <div data-testid="day-review-page">
      <PageHeader title="Day Review" subtitle={history ? `${trips.length} trips filed in history` : `${trips.length} unfinished trip${trips.length === 1 ? "" : "s"} from earlier days`}>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded-lg border border-slate-700 bg-slate-900 p-1">{tab("review", `Review${data.review_count != null ? ` (${data.review_count})` : ""}`)}{tab("history", "History")}</div>
          <button onClick={startDay} disabled={!!busy} data-testid="day-review-start-day" title="All trips from today move to Day Review (nothing unfinished stays on the board), old alerts clear and tomorrow's standing orders are added."
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-200 hover:bg-amber-500/20">
            {busy === "start" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sunrise className="h-3.5 w-3.5" />} Start New Day
          </button>
          <button onClick={sendTest} disabled={!!busy} data-testid="day-review-test-email" className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-medium text-slate-200 hover:bg-slate-800">
            <Mail className="h-3.5 w-3.5" /> Email me this now
          </button>
        </div>
      </PageHeader>
      <div className="mb-4 flex items-start gap-2 rounded-lg border border-slate-800 bg-slate-900/60 px-4 py-3 text-xs text-slate-300" data-testid="day-review-info">
        <Sun className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
        <span>Every night at midnight (company time) all trips from today move to Day Review, so nothing unfinished stays on the board. Old alerts clear and tomorrow's standing orders are added. Start New Day does the same thing early.
          {data.in_progress ? ` ${data.in_progress} trip${data.in_progress === 1 ? " is" : "s are"} still in progress on the board.` : ""}
          {last ? ` Last reset: ${fmtDayKey(last.date || dayKey(last.at))}.` : ""}</span>
      </div>
      <Panel testid="day-review-list">
        {trips.length === 0 ? (
          <p className="px-5 py-12 text-center text-sm text-slate-400" data-testid="day-review-empty">{history ? "Nothing filed in history yet." : "All clear — no unfinished trips from earlier days."}</p>
        ) : (
          <>
            {!history && (
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-800 px-5 py-3">
                <label className="mr-auto flex cursor-pointer items-center gap-2 text-xs text-slate-300">
                  <input type="checkbox" checked={allOn} onChange={() => setSel(allOn ? {} : Object.fromEntries(trips.map((t) => [t.id, true])))} className="h-4 w-4 accent-blue-600" data-testid="day-review-select-all" /> Select all ({chosen.length} selected)
                </label>
                <Button size="sm" disabled={!chosen.length || !!busy} onClick={() => setBulkOpen(true)} className="gap-1.5 bg-emerald-700 text-white hover:bg-emerald-800 disabled:bg-slate-800 disabled:text-slate-500" data-testid="day-review-close-out">
                  <CheckCheck className="h-3.5 w-3.5" /> Close out {chosen.length || ""}…
                </Button>
                <input type="date" value={moveTo || today} min={today} onChange={(e) => setMoveTo(e.target.value)} data-testid="day-review-move-date" aria-label="Move to date"
                  className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <Button size="sm" disabled={!chosen.length || !!busy || data.supports_move_date === false} onClick={() => move("today")} className="gap-1.5 bg-blue-700 text-white hover:bg-blue-800 disabled:bg-slate-800 disabled:text-slate-500" data-testid="day-review-to-today">
                  {busy === "today" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarPlus className="h-3.5 w-3.5" />} {!moveTo || moveTo === today ? "Add to today" : `Add to ${fmtDayKey(moveTo)}`}
                </Button>
                <Button size="sm" variant="outline" disabled={!chosen.length || !!busy} onClick={() => move("history")} className="gap-1.5 border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white" data-testid="day-review-to-history">
                  {busy === "history" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <History className="h-3.5 w-3.5" />} Move to history
                </Button>
              </div>
            )}
            {Object.entries(groups).sort(([a], [b]) => b.localeCompare(a)).map(([day, rows]) => (
              <div key={day} data-testid={`day-review-group-${day}`}>
                <p className="bg-slate-900/80 px-5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{day === "unknown" ? "No date" : fmtDayKey(day)} · {rows.length}</p>
                <ul className="divide-y divide-slate-800">
                  {rows.map((t) => <ReviewRow key={t.id} t={t} history={history} checked={!!sel[t.id]} onToggle={() => setSel((s) => ({ ...s, [t.id]: !s[t.id] }))} onSetStatus={() => setStatusTrip(t)} />)}
                </ul>
              </div>
            ))}
          </>
        )}
      </Panel>
      <SetStatusDialog trip={statusTrip} open={!!statusTrip} onOpenChange={(v) => { if (!v) setStatusTrip(null); }} onDone={after} />
      <SetStatusDialog tripIds={chosen} open={bulkOpen} onOpenChange={setBulkOpen} onDone={after} />
    </div>
  );
}
